import { cx } from '@emotion/css';
import { forwardRef, useEffect } from 'react';
import { useController } from 'react-hook-form';
import Autocomplete from '@mui/material/Autocomplete';
import InputLabel from '@mui/material/InputLabel';

import mergeRefs from '@julofinance/web-helpers/dist/fn/mergeRefs';
import callAllFn from '@julofinance/web-helpers/dist/fn/callAllFn';

import FieldGroup from '../field-group';

import { AutocompleteHookFormProps } from './types';
import { selectCx } from './styles';
import { TextField } from '@mui/material';

const AutocompleteHookForm = forwardRef<
  HTMLDivElement,
  AutocompleteHookFormProps
>((props, ref) => {
  const {
    formContext,
    name,
    defaultValue,
    required,
    rules,
    helperText,
    label,
    value,
    onChange,
    onBlur,
    className,
    selectClassName,
    ref: selectRef = null,
    options,
    ...resProps
  } = props;

  const { control, setValue } = formContext;
  const {
    fieldState: { error, invalid },
    field: {
      ref: controlRef,
      onChange: controllerOnChange,
      onBlur: controllerOnBlur,
      value: controllerValue,
    },
  } = useController({
    name,
    control,
    defaultValue,
    rules: {
      ...(required && { required: 'This field is required' }),
      ...(rules && rules),
    },
  });

  useEffect(() => {
    if (typeof defaultValue !== 'undefined') {
      setValue(name, defaultValue);
    }
    // This only run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * @todo will revert back when using new design
   */
  return (
    <FieldGroup
      invalid={invalid}
      error={error}
      helperText={helperText}
      className={className}
      ref={ref}
      name={name}
    >
      <InputLabel style={{ fontSize: 13 }}>{label}</InputLabel>
      <Autocomplete
        {...resProps}
        disablePortal
        className={cx(selectClassName, selectCx)}
        options={options}
        data-filled={Boolean(value)}
        ref={mergeRefs(controlRef, selectRef)}
        value={value ?? controllerValue}
        onChange={(event, newValue, reason, details) => {
          if (onChange) {
            onChange(event, newValue, reason, details);
          }
          controllerOnChange(newValue);
        }}
        onBlur={callAllFn(onBlur, controllerOnBlur)}
        renderInput={(params) => (
          <TextField
            {...params}
            autoComplete='off'
            name={name}
            variant='standard'
          />
        )}
      />
    </FieldGroup>
  );
});

export default AutocompleteHookForm;
